import React, { useCallback, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Filter, Button } from "pepsico-ds";
import { useSelector, useDispatch } from "react-redux";
import { styles } from "./searchFilterStyle";
import { useNavigate } from "react-router-dom";
import CustomSearch from "../../../utils/CustomSearch/CustomSearch";
import GridOnIcon from "@mui/icons-material/GridOn";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import { buttonStyle } from "../../InternalPages/InternalStyle";
import { toggleRefreshSearchList } from "../../../slices/searchResultSlice";
import { useQueryClient } from "@tanstack/react-query";

const sortOptions = [
  {
    id: "relevance_desc",
    label: "Relevance - highest first",
    value: "relevance_desc",
  },
  {
    id: "relevance_asc",
    label: "Relevance - lowest first",
    value: "relevance_asc",
  },
  {
    id: "pubdate_desc",
    label: "Publication date (recent first)",
    value: "pubdate_desc",
  },
  {
    id: "pubdate_asc",
    label: "Publication date (oldest first)",
    value: "pubdate_asc",
  },
  {
    id: "uploaddate_desc",
    label: "Upload date (recent first)",
    value: "uploaddate_desc",
  },
  {
    id: "uploaddate_asc",
    label: "Upload date (oldest first)",
    value: "uploaddate_asc",
  },
];

const timePeriodOptions = [
  {
    id: "1",
    label: "All",
    value: "ALL",
  },
  {
    id: "2",
    label: "Last 5 years",
    value: "LAST_5_YEARS",
  },
  {
    id: "3",
    label: "Last 10 years",
    value: "LAST_10_YEARS",
  },
  {
    id: "4",
    label: "Prior to 10 years",
    value: "PRIOR_TO_10_YEARS",
  },
];

const SearchFilter = ({
  setSearch,
  setVariety,
  setSector,
  setFlesh,
  setSelectedValue,
  selectedValue,
  searchText,
  setReload,
  reload,
  grid,
  setGrid,
  setLoad,
  variety,
  sector,
  flesh,
  setSorted,
  sort,
  year,
  setYear,
}) => {
  const isDarkTheme = useSelector((state) => state.theme);
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery("(max-width:1200px)");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Local state for search input to ensure immediate updates
  const [localSearch, setLocalSearch] = useState(searchText || "");
  const searchRef = useRef(searchText);

  // Keep ref in sync with prop
  useEffect(() => {
    searchRef.current = searchText;
  }, [searchText]);

  // Sync local state with prop when prop changes
  useEffect(() => {
    if (searchText !== localSearch) {
      setLocalSearch(searchText || "");
    }
  }, [searchText]);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["smartSearch"] });
  }, [sector, flesh, year, sort, variety, queryClient]);

  const handleSelect = (view) => {
    setGrid(view);
  };

  const handleChange = (event) => {
    setSelectedValue(event.target.value);
  };

  const handleSelectedSector = (sectorName) => {
    setSector(sectorName);
  };

  const handleVariety = (sectorName) => {
    setVariety(sectorName);
  };

  const handleFreshColor = (sectorName) => {
    setFlesh(sectorName);
  };

  const handleSearch = (value) => {
    // Update local state immediately for responsive UI
    setLocalSearch(value);

    // Debounce the prop update to prevent too many re-renders
  };

  const handleSorting = (value) => {
    setSorted(value);
  };

  const handlePublicationYear = (value) => {
    setYear(value);
  };

  // Handle navigation with up-to-date search value
  const handleNavigate = useCallback(() => {
    // Use the ref value which is always up-to-date
    const currentSearch = localSearch;

    if (currentSearch && currentSearch.trim()) {
      navigate(`/search-results`, {
        state: currentSearch,
      });
      setLoad(true);
      setFlesh(null);
      setVariety(null);
      setSector(null);
      setYear("");
      setSorted("");
      setReload(!reload);
      setLocalSearch("");

      dispatch(toggleRefreshSearchList());
    }
  }, [
    navigate,
    reload,
    setLoad,
    setFlesh,
    setVariety,
    setSector,
    setYear,
    setSorted,
    setReload,
    dispatch,
    localSearch,
  ]);

  // ENTER key search - only listen when search is open
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "Enter") {
        // Prevent default form submission
        event.preventDefault();

        // Get current value from ref
        if (searchRef.current || localSearch) {
          handleNavigate();
        }
      }
    };

    window.addEventListener("keypress", handleKeyPress);
    return () => {
      window.removeEventListener("keypress", handleKeyPress);
    };
  }, [handleNavigate, localSearch]);

  const { sectorFilter, varietyFilter, flashColorFilter } = useSelector(
    (store) => store.internalFilter
  );

  return (
    <Box
      sx={
        isMobile
          ? {
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: "24px",
            }
          : styles.container
      }
    >
      {/* Filters */}
      <Box sx={styles.filterWrapper}>
        <Filter
          label={"Sector"}
          selection="single"
          size="medium"
          isDark={isDarkTheme}
          value={sector}
          onChange={handleSelectedSector}
          options={sectorFilter.map((item) => ({
            id: item.id,
            label: item.name,
          }))}
        />

        <Filter
          label={"Variety"}
          selection="single"
          size="medium"
          value={variety}
          isDark={isDarkTheme}
          onChange={handleVariety}
          options={varietyFilter.map((item) => ({
            id: item.id,
            label: item.name,
          }))}
        />

        <Filter
          label={"Flesh color"}
          selection="single"
          size="medium"
          value={flesh}
          isDark={isDarkTheme}
          onChange={handleFreshColor}
          options={flashColorFilter.map((item) => ({
            id: item.id,
            label: item.name,
          }))}
        />
        <Filter
          label={"Publication Year"}
          selection="single"
          size="medium"
          value={year}
          isDark={isDarkTheme}
          onChange={handlePublicationYear}
          options={timePeriodOptions}
        />
        <Filter
          label={"Sort"}
          selection="single"
          size="medium"
          value={sort}
          isDark={isDarkTheme}
          onChange={handleSorting}
          options={sortOptions}
        />
      </Box>

      {/* Search and view buttons */}
      <Box
        sx={
          isMobile
            ? {
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                width: "100%",
              }
            : {
                display: "flex",
                flexDirection: "row",
                gap: "16px",
                alignItems: "flex-end",
              }
        }
      >
        {/* Search + Grid */}
        <Box
          sx={
            isMobile
              ? {
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "16px",
                }
              : {
                  display: "flex",
                  flexDirection: "row",
                  gap: "16px",
                  alignItems: "flex-end",
                }
          }
        >
          <CustomSearch
            handleNavigate={handleNavigate}
            handleSearch={handleSearch}
            searchText={localSearch} // Use local state for responsive input
          />

          <Box sx={{ display: "flex", flexDirection: "row", gap: "8px" }}>
            {/* Tile button */}
            <Button
              onClick={() => handleSelect("tile")}
              style={{
                ...buttonStyle,
                backgroundColor: grid === "tile" ? "#005CBC" : "transparent",
                color: grid === "tile" ? "#fff" : "#005CBC",
                border: grid === "tile" ? "none" : "1px solid #D7E5F8",
              }}
            >
              <GridOnIcon
                sx={{ color: grid === "tile" ? "#fff" : "#005CBC" }}
              />
            </Button>

            {/* List button */}
            <Button
              onClick={() => handleSelect("list")}
              style={{
                ...buttonStyle,
                backgroundColor: grid !== "tile" ? "#005CBC" : "transparent",
                color: grid !== "tile" ? "#fff" : "#005CBC",
                border: grid !== "tile" ? "none" : "1px solid #D7E5F8",
              }}
            >
              <ViewListOutlinedIcon
                sx={{ color: grid !== "tile" ? "#fff" : "#005CBC" }}
              />
            </Button>
          </Box>
        </Box>

        {/* Sorting dropdown */}
        <Box
          sx={
            isMobile
              ? {
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  gap: "8px",
                  maxWidth: "400px",
                }
              : styles.resultBox
          }
        >
          <Typography
            sx={
              isDarkTheme
                ? { ...styles.typography, color: "white" }
                : styles.typography
            }
          >
            Show results by
          </Typography>

          <FormControl fullWidth data-testid="dropdown">
            <Select
              value={selectedValue}
              onChange={handleChange}
              displayEmpty
              sx={styles.select(isDarkTheme)}
            >
              <MenuItem value="exactMatch" sx={styles.menuItem(isDarkTheme)}>
                Top Results
              </MenuItem>

              <MenuItem
                value="recommendedMatch"
                sx={styles.menuItem(isDarkTheme)}
              >
                Recommended
              </MenuItem>

              <MenuItem
                value="historicalMatch"
                sx={styles.menuItem(isDarkTheme)}
              >
                Historical
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

SearchFilter.propTypes = {
  setSearch: PropTypes.func.isRequired,
  setVariety: PropTypes.func.isRequired,
  setSector: PropTypes.func.isRequired,
  setFlesh: PropTypes.func.isRequired,
  setSelectedValue: PropTypes.func.isRequired,
  selectedValue: PropTypes.string.isRequired,
  searchText: PropTypes.string.isRequired,
  setReload: PropTypes.func.isRequired,
  reload: PropTypes.bool.isRequired,
  setGrid: PropTypes.func.isRequired,
  grid: PropTypes.string.isRequired,
};

export default SearchFilter;
